import { useAuth } from "@/_core/hooks/useAuth";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertCircle, Shield, Settings, Download, Copy } from "lucide-react";
import { useLocation } from "wouter";
import { SupportSnapshotPanel } from "@/components/adminHealth/SupportSnapshotPanel";
import { BuildInfoPanel } from "@/components/adminHealth/BuildInfoPanel";
import { GoLiveChecklistPanel } from "@/components/adminHealth/GoLiveChecklistPanel";
import { PanelErrorBoundary } from "@/components/_core/PanelErrorBoundary";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useState, useMemo } from "react";

const smokeCommand = 'npm run smoke:test';

export default function AdminHealth() {
  const { user, loading: authLoading } = useAuth();
  const [location] = useLocation();
  const [copyingJson, setCopyingJson] = useState(false);

  // Parse query param for safe mode
  const urlParams = useMemo(() => {
    const url = new URL(window.location.href);
    return {
      safe: url.searchParams.get('safe') === '1'
    };
  }, [location]);

  // Always fetch snapshot data (safe mode only affects UI rendering)
  const { data: snapshot, isLoading, error, refetch } = trpc.system.getAdminHealthSnapshot.useQuery(undefined, {
    staleTime: 30000, // Refetch every 30 seconds
  });

  const handleCopySnapshot = async () => {
    if (!snapshot) return;

    try {
      setCopyingJson(true);
      const jsonString = JSON.stringify(snapshot, null, 2);
      await navigator.clipboard.writeText(jsonString);
      toast.success("Snapshot JSON copied to clipboard", {
        description: "Ready for pilot debugging"
      });
    } catch (error) {
      toast.error("Failed to copy snapshot", {
        description: "Could not access clipboard"
      });
    } finally {
      setCopyingJson(false);
    }
  };

  if (authLoading) {
    return (
      <div className="container max-w-6xl py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <Shield className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!user || user.role !== "admin") {
    return (
      <div className="container max-w-6xl py-8">
        <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
          <Alert variant="destructive" className="max-w-md">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Admin Access Required</strong><br />
              This page contains advanced diagnostic tools and system management features. Only administrators can access this section.
            </AlertDescription>
          </Alert>
          <p className="text-sm text-muted-foreground">
            Contact your administrator for access to these diagnostic tools.
          </p>
        </div>
      </div>
    );
  }

  if (urlParams.safe) {
    return (
      <div className="container max-w-6xl py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Settings className="h-8 w-8" />
              Admin Health Dashboard
            </h1>
            <p className="text-muted-foreground mt-1">
              Advanced diagnostic tools and system management controls
            </p>
            <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md dark:bg-yellow-950 dark:border-yellow-800">
              <strong>Safe Mode Enabled</strong> - Panels are not loaded to isolate crashes.
              <div className="mt-1 text-sm">Remove <code>?safe=1</code> from URL to load panels with error boundaries.</div>
            </div>
          </div>
        </div>

        <div className="grid gap-4">
          <div className="text-lg font-semibold">Available Panels:</div>
          <div className="grid grid-cols-1 gap-2">
            <div className="p-4 border rounded-md">Build Information</div>
            <div className="p-4 border rounded-md">Support Snapshot</div>
            <div className="p-4 border rounded-md">Go-Live Checklist</div>
          </div>
        </div>

        <div className="mt-8 p-4 bg-muted rounded-lg">
          <h3 className="font-semibold mb-2">Admin Privileges Required</h3>
          <p className="text-sm text-muted-foreground">
            Advanced diagnostic tools and system management capabilities.
            Use safe mode to isolate panel crashes during debugging.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container max-w-6xl py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Settings className="h-8 w-8" />
            Admin Health Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">
            Advanced diagnostic tools and system management controls
          </p>
          <div className="mt-2 text-xs text-muted-foreground">
            Error boundaries enabled - crashed panels will show error information
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => refetch()}
            variant="outline"
            size="sm"
            disabled={isLoading}
          >
            <Download className="h-4 w-4 mr-2" />
            {isLoading ? 'Refreshing...' : 'Refresh All'}
          </Button>
          <Button
            onClick={handleCopySnapshot}
            variant="outline"
            size="sm"
            disabled={copyingJson || !snapshot}
          >
            <Copy className="h-4 w-4 mr-2" />
            {copyingJson ? 'Copying...' : 'Copy Snapshot JSON'}
          </Button>
        </div>
      </div>

      <div className="grid gap-6">
        {/* Top row - core diagnostic tools */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <PanelErrorBoundary name="Build Information">
            <BuildInfoPanel
              snapshotData={snapshot ? { app: snapshot.app, build: snapshot.build } : undefined}
              generatedAtISO={snapshot?.generatedAtISO}
              onRefresh={refetch}
            />
          </PanelErrorBoundary>
          <PanelErrorBoundary name="Support Snapshot">
            <SupportSnapshotPanel
              snapshotData={snapshot ? {
                app: snapshot.app,
                db: snapshot.db,
                storage: snapshot.storage,
                features: snapshot.features
              } : undefined}
              fullSnapshot={snapshot}
            />
          </PanelErrorBoundary>
        </div>

        {/* Bottom row - system management */}
        <PanelErrorBoundary name="Go-Live Checklist">
          <GoLiveChecklistPanel
            goLiveSnapshot={snapshot?.goLiveStatusSnapshot}
            generatedAtISO={snapshot?.generatedAtISO}
            onRefresh={refetch}
            userRole={user?.role}
          />
        </PanelErrorBoundary>
        <PanelErrorBoundary name="Attachments Health">
          <Card>
            <CardHeader>
              <CardTitle>Uploads & Attachments</CardTitle>
              <CardDescription>Latest attachment, directory stats, orphan records</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {snapshot?.attachmentsHealth ? (
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium">Latest Attachment</h4>
                    {snapshot.attachmentsHealth.latestAttachment ? (
                      <div className="text-sm space-y-1">
                        <div>ID: {snapshot.attachmentsHealth.latestAttachment.id}</div>
                        <div>File: {snapshot.attachmentsHealth.latestAttachment.fileKey}</div>
                        <div>Exists on disk: {snapshot.attachmentsHealth.latestAttachment.existsOnDisk ? '✓' : '✗'}</div>
                        {snapshot.attachmentsHealth.latestAttachment.sizeBytes && (
                          <div>Size: {(snapshot.attachmentsHealth.latestAttachment.sizeBytes / 1024 / 1024).toFixed(1)} MB</div>
                        )}
                        {snapshot.attachmentsHealth.latestAttachment.mtimeISO && (
                          <div>Modified: {new Date(snapshot.attachmentsHealth.latestAttachment.mtimeISO).toLocaleString()}</div>
                        )}
                      </div>
                    ) : (
                      <div className="text-sm text-muted-foreground">No attachments</div>
                    )}
                  </div>
                  <div>
                    <h4 className="font-medium">Uploads Directory</h4>
                    <div className="text-sm space-y-1">
                      <div>Exists: {snapshot.attachmentsHealth.uploadsDir.exists ? '✓' : '✗'}</div>
                      <div>Files: {snapshot.attachmentsHealth.uploadsDir.fileCount}</div>
                      {snapshot.attachmentsHealth.uploadsDir.newestFileISO && (
                        <div>Newest: {new Date(snapshot.attachmentsHealth.uploadsDir.newestFileISO).toLocaleString()}</div>
                      )}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium">Orphans</h4>
                    <div className="text-sm space-y-1">
                      <div>logAttachments: {snapshot.attachmentsHealth.orphans.logAttachments}</div>
                      <div>chemicalLogs: {snapshot.attachmentsHealth.orphans.chemicalLogs}</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">Loading...</div>
              )}
            </CardContent>
          </Card>
        </PanelErrorBoundary>
        <PanelErrorBoundary name="Smoke Test">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                Smoke Test
              </CardTitle>
              <CardDescription>
                Verify core functionality and RBAC. Run locally on server.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-sm font-mono bg-muted p-3 rounded-md">
                npm run smoke:test
              </div>
              <div className="text-xs text-muted-foreground">
                <strong>Run smoke test:</strong><br />
                <code>{smokeCommand}</code>
              </div>
              <div className="text-xs text-muted-foreground">
                Runs locally on the server.
                If SMOKE_SUPERVISOR_* and SMOKE_OPERATOR_* credentials are set (e.g. in .env),
                the smoke test will also validate RBAC for supervisor and operator roles.
                This UI does not execute commands.
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(smokeCommand);
                  toast.success('Command copied');
                }}
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy Command
              </Button>
            </CardContent>
          </Card>
        </PanelErrorBoundary>
      </div>

      <div className="mt-8 p-4 bg-muted rounded-lg">
        <h3 className="font-semibold mb-2">Admin Privileges Required</h3>
        <p className="text-sm text-muted-foreground">
          These tools provide deep system insights and configuration management capabilities.
          Changes made here affect system behavior and monitoring. Use with caution and consult
          documentation before making modifications.
        </p>
      </div>
    </div>
  );
}
